import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { HERO, SITE } from "@/data/content";
import { Diamond, PlayStoreIcon } from "@/components/decor";
import { MOTION } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const rise = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { ...MOTION, delay },
});

const renderWithBrand = (text) =>
  text.split("Infra Digest").map((part, i, arr) => (
    <span key={i}>
      {part}
      {i < arr.length - 1 && <strong className="font-semibold text-ink">Infra Digest</strong>}
    </span>
  ));

export default function Hero() {
  return (
    <section id="home" data-testid="home-section" className="relative overflow-hidden bg-paper">
      <div
        className="absolute inset-0 bg-blueprint [mask-image:radial-gradient(ellipse_70%_65%_at_50%_40%,black,transparent_78%)]"
        aria-hidden="true"
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-16 pt-28 sm:px-8 lg:min-h-[92vh] lg:grid-cols-12 lg:gap-10 lg:pb-24 lg:pt-36">
        <div className="lg:col-span-7">
          <motion.div {...rise(0)} className="flex items-center gap-3">
            <Diamond className="bg-signal" />
            <span className="text-micro uppercase text-accent sm:text-label">{HERO.eyebrow}</span>
          </motion.div>

          <motion.h1
            {...rise(0.1)}
            className="mt-6 font-display text-display-sm font-bold tracking-tight text-ink md:text-display"
          >
            {HERO.titleLines.map((l, i) => (
              <span key={i} className={`block ${l.className || ""}`}>
                {l.text}
              </span>
            ))}
          </motion.h1>

          <motion.div {...rise(0.2)} className="mt-6 max-w-xl space-y-4">
            {HERO.paragraphs.map((p, i) => (
              <p key={p.slice(0, 24)} className="text-body-sm text-ink/65 md:text-body">
                {i === 1 ? renderWithBrand(p) : p}
              </p>
            ))}
            <p className="pt-3 text-lg font-medium leading-relaxed text-ink">{HERO.closing}</p>
          </motion.div>

          <motion.div {...rise(0.3)} className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
            <a
              href={SITE.playStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-play-button"
              className="group inline-flex h-14 w-full items-center justify-center gap-3 rounded-2xl bg-ink px-6 text-paper transition-colors duration-300 hover:bg-accent sm:w-auto sm:min-w-btn"
            >
              <PlayStoreIcon className="h-6 w-6" />
              <span className="text-left leading-tight">
                <span className="block text-label uppercase text-paper/60">Get it on</span>
                <span className="block font-display text-base font-bold">Google Play</span>
              </span>
            </a>
            <button
              type="button"
              onClick={() => scrollToId("#sectors")}
              data-testid="hero-explore-button"
              className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-2xl border border-ink/15 bg-paper/60 px-6 text-sm font-semibold text-ink backdrop-blur transition-colors duration-300 hover:border-accent hover:text-accent sm:w-auto sm:min-w-btn"
            >
              {HERO.secondaryCta} <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div {...rise(0.15)} className="relative">
            <div className="overflow-hidden rounded-3xl border border-line shadow-[0_48px_90px_-40px_rgba(11,25,44,0.4)]">
              <img
                src={HERO.image}
                srcSet={`${HERO.image} 768w, ${HERO.image2x} 1536w`}
                sizes="(min-width: 1024px) 480px, 100vw"
                width="1536"
                height="1024"
                alt={HERO.imageAlt}
                loading="eager"
                fetchPriority="high"
                className="h-auto w-full"
              />
            </div>
            <motion.div
              {...rise(0.5)}
              className="absolute -bottom-5 left-4 rounded-2xl bg-ink px-5 py-4 text-paper shadow-xl sm:left-6"
              data-testid="hero-domains-chip"
            >
              <span className="block font-display text-3xl font-bold leading-none">
                {HERO.bigChip.value}
              </span>
              <span className="mt-1.5 block text-label uppercase text-paper/60">
                {HERO.bigChip.label}
              </span>
            </motion.div>
            <motion.div
              {...rise(0.6)}
              className="absolute -top-4 right-4 rounded-full border border-line bg-paper/90 px-4 py-2 text-label uppercase text-accent shadow-lg backdrop-blur"
            >
              {HERO.smallChip}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
